import { platformById, type SampleDeal } from './data';
import { ago, cd, fD, fDS, pct, short, spoken } from './format';

/**
 * Everything a card needs to say about a raise at one instant: its status,
 * how urgent its deadline is, the countdown, how full it is and whether the
 * viewer can invest. Pure, so the server render, the ticking browser and the
 * tests all agree.
 *
 * Urgency follows design/design-tokens.md: ≤ 72 hours critical (■), ≤ 7 days
 * urgent (▲), anything later calm (○). The glyph always travels with the
 * colour, so urgency never rests on colour alone.
 */

export type Urgency = 'critical' | 'urgent' | 'calm';
export type Status =
  'upcoming' | 'live' | 'closing' | 'nearcap' | 'extended' | 'closed';
/** The token family a badge is drawn in. */
export type Tone =
  'critical' | 'urgent' | 'cap' | 'live' | 'neutral' | 'upcoming';

export interface Deco {
  status: Status;
  statusLabel: string;
  statusGlyph: string;
  statusTone: Tone;
  urgency: Urgency;
  urgencyGlyph: '■' | '▲' | '○';
  msLeft: number;
  countdown: string;
  spoken: string;
  closeShort: string;
  /** Raised as a share of the maximum (or the target, with no maximum). */
  pct: number | null;
  /** "99.9% of €2.5M". */
  pctLabel: string | null;
  /** Bar fill, 0–100, and its step on the red → amber → green ramp. */
  fill: number;
  fillStep: 'low' | 'mid' | 'high';
  /** Where the target tick sits on the bar, 0–100, or null with no maximum. */
  targetAt: number | null;
  nearCap: boolean;
  eligible: boolean;
  eligibility: string;
  provenance: string;
}

const H = 3_600_000;
export const CRITICAL_MS = 72 * H;
export const URGENT_MS = 7 * 24 * H;
export const NEAR_CAP = 0.9;

export function urgencyOf(msLeft: number): Urgency {
  if (msLeft <= CRITICAL_MS) return 'critical';
  if (msLeft <= URGENT_MS) return 'urgent';
  return 'calm';
}

export const URGENCY_GLYPH = {
  critical: '■',
  urgent: '▲',
  calm: '○',
} as const;

const ELIGIBILITY = {
  foreign: 'Open to foreign investors',
  residents: 'Residents only',
  accredited: 'Accredited only',
} as const;

/** Whether someone living in `cc` can invest. */
export function canInvest(deal: SampleDeal, cc: string | null): boolean {
  if (deal.openTo === 'foreign') return true;
  if (deal.openTo === 'residents') return cc === deal.country;
  return false;
}

export function deco(
  deal: SampleDeal,
  now: number,
  options: { cc?: string | null } = {},
): Deco {
  const cc = options.cc ?? null;
  const msLeft = deal.closesAt - now;
  const urgency = urgencyOf(msLeft);
  const scale = deal.max ?? deal.target;
  const ratio = deal.raised !== null && scale ? deal.raised / scale : null;
  const nearCap =
    deal.max !== null &&
    deal.raised !== null &&
    deal.raised / deal.max >= NEAR_CAP;

  let status: Status;
  if (msLeft <= 0) status = 'closed';
  else if (deal.launchAt > now) status = 'upcoming';
  // Near cap wins the status over closing soon: the countdown beside it
  // already carries the deadline (design/README.md, Watchlist).
  else if (nearCap) status = 'nearcap';
  else if (urgency !== 'calm') status = 'closing';
  else if (deal.extended) status = 'extended';
  else status = 'live';

  const badge = STATUS_BADGE[status](urgency);
  const fill = ratio === null ? 0 : Math.min(100, ratio * 100);
  const eligible = canInvest(deal, cc);

  return {
    status,
    statusLabel: badge.label,
    statusGlyph: badge.glyph,
    statusTone: badge.tone,
    urgency,
    urgencyGlyph: URGENCY_GLYPH[urgency],
    msLeft,
    countdown: cd(msLeft),
    spoken: spoken(msLeft),
    closeShort: fD(deal.closesAt),
    pct: ratio === null ? null : ratio * 100,
    pctLabel:
      ratio === null || !scale
        ? null
        : `${pct(ratio * 100)} of ${short(scale, deal.currency)}`,
    fill,
    fillStep: fill < 34 ? 'low' : fill < 67 ? 'mid' : 'high',
    targetAt:
      deal.max && deal.target
        ? Math.min(100, (deal.target / deal.max) * 100)
        : null,
    nearCap,
    eligible,
    eligibility:
      eligible || cc === null
        ? ELIGIBILITY[deal.openTo]
        : `Not available in ${cc}`,
    provenance: deal.filed
      ? `${deal.filed.form} · filed ${fDS(deal.filed.at)}`
      : `${platformById(deal.platform).name} · checked ${ago(deal.checkedAt, now)}`,
  };
}

/** Each status's badge: its word, glyph and token family. */
export const STATUS_BADGE: Record<
  Status,
  (u: Urgency) => { label: string; glyph: string; tone: Tone }
> = {
  closed: () => ({ label: 'Closed', glyph: '○', tone: 'neutral' }),
  upcoming: () => ({ label: 'Upcoming', glyph: '○', tone: 'upcoming' }),
  nearcap: () => ({ label: 'Near cap', glyph: '◆', tone: 'cap' }),
  closing: (u) => ({
    label: 'Closing soon',
    glyph: URGENCY_GLYPH[u],
    tone: u === 'critical' ? 'critical' : 'urgent',
  }),
  extended: () => ({ label: 'Extended', glyph: '↻', tone: 'neutral' }),
  live: () => ({ label: 'Live', glyph: '●', tone: 'live' }),
};

export function isLive(deal: SampleDeal, now: number): boolean {
  return deal.launchAt <= now && deal.closesAt > now;
}
